import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Wand2, Coins, PawPrint } from "lucide-react";
import { api, fileUrl, formatError, promptImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AddPetDialog } from "@/components/AddPetDialog";
import { DropCountdown } from "@/components/DropCountdown";

const CATS = ["All", "New", "Vintage", "Sci-Fi", "Cute", "Cyberpunk"];

export default function Studio() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [pets, setPets] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [petId, setPetId] = useState(null);
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/pets").then((r) => { setPets(r.data); setPetId(r.data[0]?.pet_id || null); });
    api.get("/prompts").then((r) => setPrompts(r.data));
  }, []);

  const focus = params.get("prompt");
  const list = prompts.filter((p) => cat === "All" || (cat === "New" ? p.is_new : p.category === cat));

  const generate = async (prompt) => {
    if (!petId) { setOpen(true); return toast("Add a pet first so we know who the star is!"); }
    setBusy(prompt.id);
    try {
      const { data } = await api.post("/generations", { pet_id: petId, prompt_id: prompt.id });
      refresh();
      nav(`/creation/${data.generation_id}`);
    } catch (err) {
      toast.error(formatError(err));
      if (err?.response?.status === 402) nav("/credits");
    } finally {
      setBusy(null);
    }
  };

  const removePet = async (id) => {
    await api.delete(`/pets/${id}`);
    const rest = pets.filter((p) => p.pet_id !== id);
    setPets(rest);
    if (petId === id) setPetId(rest[0]?.pet_id || null);
    toast.success("Pet removed");
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="studio-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-sm font-semibold text-[#6E687A]">Hey {user?.name?.split(" ")[0]}</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">The Studio</h1>
        </div>
        <div className="flex items-center gap-2 rounded-2xl bg-white border border-[#EAE3D9] px-4 py-3 text-sm" data-testid="studio-credit-meter">
          <Coins className="h-5 w-5 text-[#FFC72C]" />
          <span><b>{user?.free_credits}</b> free today · <b>{user?.paid_credits}</b> bonus</span>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="text-lg font-semibold">1. Choose your star</h2>
        <div className="mt-4 flex gap-4 overflow-x-auto pb-3" data-testid="pet-selector">
          {pets === null && [0, 1, 2].map((i) => <div key={i} className="shimmer h-36 w-32 shrink-0 rounded-3xl" />)}
          {pets?.map((p) => (
            <div key={p.pet_id} className="relative group shrink-0">
              <button onClick={() => setPetId(p.pet_id)} data-testid={`pet-card-${p.pet_id}`}
                className={`w-32 rounded-3xl p-2 bg-white text-left transition-[transform,box-shadow] duration-200 ${petId === p.pet_id ? "sticker -rotate-2" : "border border-[#EAE3D9] hover:-translate-y-1"}`}>
                <img src={fileUrl(p.photo_path)} alt={p.name} className="h-24 w-full object-cover rounded-2xl" />
                <p className="mt-2 px-1 font-display font-semibold truncate">{p.name}</p>
                <p className="px-1 text-xs text-[#6E687A] capitalize truncate">{p.breed || p.species}</p>
              </button>
              <button onClick={() => removePet(p.pet_id)} data-testid={`pet-delete-${p.pet_id}`} aria-label="Remove pet"
                className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-white border border-[#EAE3D9] grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
          ))}
          <button onClick={() => setOpen(true)} data-testid="add-pet-button"
            className="shrink-0 w-32 h-[9.5rem] rounded-3xl border-2 border-dashed border-[#EAE3D9] hover:border-[#FF5A79] hover:text-[#FF5A79] grid place-items-center transition-colors">
            <span className="text-center"><Plus className="h-7 w-7 mx-auto" /><span className="text-sm font-bold">Add pet</span></span>
          </button>
        </div>
        {pets?.length === 0 && (
          <p className="mt-2 text-sm text-[#6E687A] flex items-center gap-2"><PawPrint className="h-4 w-4" /> Add your first pet to start creating.</p>
        )}
      </section>

      <section className="mt-12">
        <DropCountdown newCount={prompts.filter((p) => p.is_new).length} />
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-lg font-semibold">2. Pick a viral style</h2>
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`category-pill-${c.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${cat === c ? "bg-[#8A4FFF] text-white border-[#8A4FFF]" : "bg-white border-[#EAE3D9] hover:border-[#8A4FFF]"}`}>{c}</button>
            ))}
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {list.map((p, i) => (
            <article key={p.id} data-testid={`prompt-card-${p.id}`} style={{ animationDelay: `${i * 50}ms` }}
              className={`rise group rounded-3xl bg-white overflow-hidden flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl ${focus === p.id ? "sticker" : "border border-[#EAE3D9]"}`}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={promptImg(p)} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">{p.tag}</span>
                {p.is_new && <span className="absolute top-3 right-3 rounded-full bg-[#FF5A79] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider sticker rotate-3" data-testid={`prompt-new-badge-${p.id}`}>New</span>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-display text-xl font-semibold">{p.title}</h3>
                <p className="mt-1 text-sm text-[#6E687A] leading-relaxed flex-1">{p.description}</p>
                <button onClick={() => generate(p)} disabled={!!busy} data-testid={`prompt-card-generate-button-${p.id}`}
                  className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#1E1B26] text-white py-2.5 text-sm font-bold hover:bg-[#FF5A79] transition-colors disabled:opacity-60">
                  <Wand2 className="h-4 w-4" /> {busy === p.id ? "Starting..." : "Generate · 1 credit"}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
      <AddPetDialog open={open} onOpenChange={setOpen} onCreated={(p) => { setPets((x) => [p, ...(x || [])]); setPetId(p.pet_id); }} />
    </main>
  );
}
